import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../lib/i18n';
import { dashboardAPI, tipsAPI, transactionsAPI, categoriesAPI } from '../lib/api';
import { formatCurrency, formatDate, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  TrendingUp, TrendingDown, Wallet, ArrowUpRight, ArrowDownRight,
  PiggyBank, CalendarDays, Lightbulb, Plus, RefreshCw, Camera
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { toast } from 'sonner';
import ScanReceiptDialog from '../components/ScanReceiptDialog';

const Dashboard = () => {
  const { user, language } = useAuth();
  const t = useTranslation(language);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [tips, setTips] = useState([]);
  const [currentTip, setCurrentTip] = useState(0);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, tipsRes] = await Promise.all([
        dashboardAPI.getSummary(),
        tipsAPI.get()
      ]);
      setDashboard(dashboardRes.data);
      setTips(tipsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (tips.length > 1) {
      const interval = setInterval(() => {
        setCurrentTip(prev => (prev + 1) % tips.length);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [tips.length]);

  const CHART_COLORS = ['#064E3B', '#D97706', '#10B981', '#3B82F6', '#8B5CF6'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="dashboard-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-900" />
      </div>
    );
  }

  const savingsPercent = dashboard?.monthly_income > 0
    ? ((dashboard.monthly_savings / dashboard.monthly_income) * 100).toFixed(1)
    : 0;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl lg:text-3xl text-slate-900 tracking-tight">
            {t('dashboard')}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Bienvenido, {user?.name}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4 sm:mt-0">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            className="rounded-sm"
            data-testid="refresh-btn"
          >
            <RefreshCw size={16} className="mr-2" />
            Actualizar
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="rounded-sm border-amber-500 text-amber-600 hover:bg-amber-50"
            onClick={() => setScanDialogOpen(true)}
            data-testid="scan-receipt-btn"
          >
            <Camera size={16} className="mr-2" />
            Escanear Gasto
          </Button>
          <Button
            size="sm"
            className="bg-emerald-900 hover:bg-emerald-800 rounded-sm"
            onClick={() => window.location.href = '/transactions?new=true'}
            data-testid="new-transaction-btn"
          >
            <Plus size={16} className="mr-2" />
            {t('newTransaction')}
          </Button>
        </div>
      </div>

      {/* Scan Receipt Dialog */}
      <ScanReceiptDialog
        open={scanDialogOpen}
        onOpenChange={setScanDialogOpen}
        onSuccess={fetchData}
      />

      {/* Main metrics - Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Balance */}
        <Card className="border-slate-200 rounded-sm shadow-none hover:border-emerald-900/30 transition-colors" data-testid="total-balance-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">
                  {t('totalBalance')}
                </p>
                <p className="font-mono text-2xl lg:text-3xl font-medium text-slate-900 tracking-tight">
                  {formatCurrency(dashboard?.total_balance || 0, user?.currency)}
                </p>
              </div>
              <div className="w-10 h-10 bg-emerald-50 rounded-sm flex items-center justify-center">
                <Wallet className="w-5 h-5 text-emerald-900" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Income */}
        <Card className="border-slate-200 rounded-sm shadow-none hover:border-emerald-900/30 transition-colors" data-testid="monthly-income-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">
                  {t('monthlyIncome')}
                </p>
                <p className="font-mono text-2xl lg:text-3xl font-medium text-emerald-600 tracking-tight">
                  {formatCurrency(dashboard?.monthly_income || 0, user?.currency)}
                </p>
              </div>
              <div className="w-10 h-10 bg-emerald-50 rounded-sm flex items-center justify-center">
                <ArrowUpRight className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Expenses */}
        <Card className="border-slate-200 rounded-sm shadow-none hover:border-emerald-900/30 transition-colors" data-testid="monthly-expenses-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">
                  {t('monthlyExpenses')}
                </p>
                <p className="font-mono text-2xl lg:text-3xl font-medium text-red-500 tracking-tight">
                  {formatCurrency(dashboard?.monthly_expenses || 0, user?.currency)}
                </p>
              </div>
              <div className="w-10 h-10 bg-red-50 rounded-sm flex items-center justify-center">
                <ArrowDownRight className="w-5 h-5 text-red-500" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Savings */}
        <Card className="border-slate-200 rounded-sm shadow-none hover:border-emerald-900/30 transition-colors" data-testid="monthly-savings-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">
                  {t('monthlySavings')}
                </p>
                <p className={cn(
                  "font-mono text-2xl lg:text-3xl font-medium tracking-tight",
                  dashboard?.monthly_savings >= 0 ? "text-emerald-600" : "text-red-500"
                )}>
                  {formatCurrency(dashboard?.monthly_savings || 0, user?.currency)}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {savingsPercent}% de ingresos
                </p>
              </div>
              <div className={cn(
                "w-10 h-10 rounded-sm flex items-center justify-center",
                dashboard?.monthly_savings >= 0 ? "bg-emerald-50" : "bg-red-50"
              )}>
                <PiggyBank className={cn(
                  "w-5 h-5",
                  dashboard?.monthly_savings >= 0 ? "text-emerald-600" : "text-red-500"
                )} strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Second row - Quick indicators + Financial tip */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Quick indicators */}
        <Card className="border-slate-200 rounded-sm shadow-none" data-testid="quick-indicators-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-slate-900">Indicadores Rápidos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <span className="text-sm text-slate-500">{t('dailyAverage')}</span>
              <span className="font-mono text-sm font-medium text-slate-900">
                {formatCurrency(dashboard?.daily_average_expense || 0, user?.currency)}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <span className="text-sm text-slate-500">{t('topCategory')}</span>
              <span className="text-sm font-medium text-slate-900">
                {dashboard?.top_expense_category || '-'}
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-500">{t('lastTransaction')}</span>
              <span className="font-mono text-sm font-medium text-slate-900">
                {dashboard?.last_transaction
                  ? formatCurrency(dashboard.last_transaction.amount, user?.currency)
                  : '-'
                }
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Financial Tip */}
        <Card className="lg:col-span-2 border-slate-200 rounded-sm shadow-none bg-gradient-to-br from-emerald-900 to-emerald-800" data-testid="financial-tip-card">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/10 rounded-sm flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-6 h-6 text-amber-400" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-widest font-semibold text-emerald-200 mb-2">
                  {t('financialTip')}
                </p>
                {tips.length > 0 && (
                  <>
                    <h3 className="text-lg font-display font-semibold text-white mb-2">
                      {tips[0]?.title}
                    </h3>
                    <p className="text-emerald-100 text-sm leading-relaxed">
                      {tips[0]?.content}
                    </p>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Income vs Expenses Chart */}
        <Card className="lg:col-span-2 border-slate-200 rounded-sm shadow-none" data-testid="income-expenses-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-900">
              {t('incomeVsExpenses')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 min-h-[200px]">
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                <AreaChart data={dashboard?.income_vs_expense_trend || []}>
                  <defs>
                    <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="expenseGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#EF4444" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    axisLine={{ stroke: '#E2E8F0' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    axisLine={{ stroke: '#E2E8F0' }}
                    tickFormatter={(v) => `${v / 1000}k`}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: '2px',
                      border: '1px solid #E2E8F0',
                      boxShadow: 'none'
                    }}
                    formatter={(value) => formatCurrency(value, user?.currency)}
                  />
                  <Area
                    type="monotone"
                    dataKey="income"
                    stroke="#10B981"
                    strokeWidth={1.5}
                    fill="url(#incomeGradient)"
                    name="Ingresos"
                  />
                  <Area
                    type="monotone"
                    dataKey="expenses"
                    stroke="#EF4444"
                    strokeWidth={1.5}
                    fill="url(#expenseGradient)"
                    name="Gastos"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Expenses by Category */}
        <Card className="border-slate-200 rounded-sm shadow-none" data-testid="expenses-category-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-900">
              {t('expensesByCategory')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48 min-h-[150px]">
              <ResponsiveContainer width="100%" height="100%" minWidth={150} minHeight={150}>
                <PieChart>
                  <Pie
                    data={dashboard?.expenses_by_category || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="amount"
                    nameKey="category"
                  >
                    {(dashboard?.expenses_by_category || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color || CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => formatCurrency(value, user?.currency)}
                    contentStyle={{
                      borderRadius: '2px',
                      border: '1px solid #E2E8F0',
                      boxShadow: 'none'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {/* Legend */}
            <div className="space-y-2 mt-4">
              {(dashboard?.expenses_by_category || []).slice(0, 4).map((cat, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: cat.color || CHART_COLORS[i] }}
                    />
                    <span className="text-slate-600 truncate">{cat.category}</span>
                  </div>
                  <span className="font-mono text-slate-900">
                    {formatCurrency(cat.amount, user?.currency)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Accounts Summary */}
      <Card className="border-slate-200 rounded-sm shadow-none" data-testid="accounts-summary-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-slate-900">
              {t('accountsSummary')}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              className="text-emerald-900 hover:text-emerald-800"
              onClick={() => window.location.href = '/accounts'}
            >
              Ver todas
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(dashboard?.accounts_summary || []).map((acc, i) => (
              <div
                key={i}
                className="p-4 border border-slate-200 rounded-sm hover:border-emerald-900/30 transition-colors"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-8 h-8 rounded-sm flex items-center justify-center"
                    style={{ backgroundColor: `${acc.color}15` }}
                  >
                    <Wallet className="w-4 h-4" style={{ color: acc.color }} strokeWidth={1.5} />
                  </div>
                  <span className="text-sm font-medium text-slate-900">{acc.name}</span>
                </div>
                <p className="font-mono text-xl font-medium text-slate-900">
                  {formatCurrency(acc.balance, user?.currency)}
                </p>
                <p className="text-xs text-slate-400 capitalize mt-1">{acc.type}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
